import {z} from 'zod';

import {
  CampaignFilter,
  CompositionScene,
  FileOperationResponseBody,
  MarketSituationEnum,
  Money,
  PriceDynamicsEnum,
  TagResponseBody,
  UuidResponseBody,
  VehicleTypeEnum,
  WatermarkPositionEnum,
} from '@dms/api/shared';

type VehicleInsuranceResponseBody = {
  customerInsurance: {
    liabilityInsurance: {
      institutionId: string | null;
      date: string | null;
      contractNumber: string | null;
    };
    collisionInsurance: {
      institutionId: string | null;
      date: string | null;
      contractNumber: string | null;
    };
    note: string | null;
  } | null;
  fleetInsurance: {
    note: string | null;
    date: string | null;
  } | null;
};

type TestDriveState = 'planned' | 'completed' | 'cancelled';
type TestDriveResponseBody = {
  id: string;
  businessCaseId: string | null;
  businessCaseCode: string | null;
  vehicleId: string;
  userId: string;
  userFirstName: string;
  userLastName: string;
  customerId: string;
  customerFoundingPersonFirstName: string | null;
  customerFoundingPersonLastName: string | null;
  customerTradeName: string | null;
  state: TestDriveState;
  note: string | null;
  scheduledAt: string;
};

export const DownloadGalleryResponseBodySchema = z.object({
  eventId: z.string(),
});
export type DownloadGalleryResponseBody = z.infer<typeof DownloadGalleryResponseBodySchema>;

type CreateVehiclePriceReportRequestBody = {
  country?: string | null;
};

type CopyAuditPhotoToAlbumRequestBody = {
  auditId: string;
  auditCategoryId: string;
  vehicleAlbumId: string;
};

type AuditResponseBody = {
  id: string;
  controlStructureId: string;
  vehicleId: string;
  name: string;
  state: string;
  updatedAt: string | null;
};

export type PriceReportResponseBody = {
  id: string;
  requestId: string;
  url: string;
  country: string;
  createdAt: string;
  createdBy?: string | null;
};

export type SalesPriceReportResponseBody = {
  id: string | null;
  requestId: string | null;
  url: string;
  country: string | null;
  createdAt: string;
  createdBy: string | null;
  sellingPrice?: number | null;
  sellingPriceCurrency?: string | null;
  sellingPriceMin?: number | null;
  sellingPriceMinCurrency?: string | null;
  sellingPriceMax?: number | null;
  sellingPriceMaxCurrency?: string | null;
  purchasePrice?: number | null;
  purchasePriceCurrency?: string | null;
  turnover?: number | null;
  featuresLevel?: number | null;
  attractivenessScore?: number | null;
  priceDynamics?: PriceDynamicsEnum | null;
  marketSituation?: MarketSituationEnum | null;
};

export type CreatePriceReportResponseBody = {
  id: string;
  url: string;
  country: string;
  createdAt: string;
};

export type DeleteVehicleApiResponse = /** status 204  */ undefined;
export type DeleteVehicleApiArg = {
  vehicleId: string;
};

export type VehicleStatisticsResponse = {
  countOnStock: number;
  countToSell: number;
  countBrokerage: number;
  countInAdvertisement: number;
  countBoughtInWeek: number;
  countSoldInWeek: number;
};
export type GetVehicleStatisticsApiResponse = /** status 200  */ VehicleStatisticsResponse;
export type GetVehicleStatisticsApiArg = {
  branchId?: string | null;
};
export type DeleteVehicleAlbumApiResponse = /** status 204  */ undefined;
export type DeleteVehicleAlbumApiArg = {
  vehicleAlbumId: string;
};
export type DeletePhotoApiResponse = unknown;
export type DeletePhotoApiArg = {
  vehicleId: string;
  albumId: string;
  vehiclePhotoDeleteRequestBody: VehiclePhotoDeleteRequestBody;
};
export type SetFeatureAsHighlightedApiResponse = unknown;
export type SetFeatureAsHighlightedApiArg = {
  vehicleId: string;
  featureKey: string;
};
export type SetFeaturesHighlightedApiArg = {
  vehicleId: string;
  features: {key: string; isHighlighted: boolean; isPremium: boolean}[];
};
export type SetFeaturesHighlightedApiRes = unknown;
export type SetFeatureAsNotHighlightedApiResponse = unknown;
export type SetFeatureAsNotHighlightedApiArg = {
  vehicleId: string;
  featureKey: string;
  authorization?: string;
};
export type RemoveVehiclePhotosBackgroundApiResponse = unknown;
export type RemoveVehiclePhotosBackgroundApiArg = {
  vehicleId: string;
  body: RemoveBackgroundRequestBody;
};
export type VehiclePhotoDto = {
  fileId: string;
};
export type VehiclePhotoDeleteRequestBody = {
  photos: VehiclePhotoDto[];
};
export type RemoveBackgroundRequestBody = {
  vehiclePhotoIds: string[];
};
export type CreateVehicleAlbumApiResponse = /** status 201  */ CreateVehicleAlbumResponseBody;

export type CreateVehicleAlbumApiArg = {
  createVehicleAlbumRequestBody: CreateVehicleAlbumRequestBody;
};
export type CreateVehicleAlbumResponseBody = {
  vehicleAlbumId: string;
};
export type CreateVehicleAlbumRequestBody = {
  vehicleId: string;
  serviceCaseId: string | null;
  name:
    | 'PHOTOS_CAR_ORIGINAL'
    | 'PHOTOS_CAR_PURCHASE'
    | 'PHOTOS_CAR_SALES'
    | 'PHOTOS_SERVICE_CASE'
    | 'PHOTOS_CUSTOM';
  description?: string | null;
  weight?: number;
  canHaveCover?: boolean;
};
export type CreateVehiclePriceReportApiResponse = /** status 202  */ CreatePriceReportResponseBody;
export type CreateVehiclePriceReportApiArg = {
  vehicleId: string;
  body: CreateVehiclePriceReportRequestBody;
};
export type GetVehiclePriceReportsApiResponse = /** status 200  */ SalesPriceReportResponseBody[];
export type GetVehiclePriceReportsApiArg = {
  vehicleId: string;
};

export type GetVehicleHighlightsApiRes = /** status 200  */ {id: string; title: string}[];
export type GetVehicleHighlightsApiArg = {
  vehicleId: string;
};
export type GetAutofillFeaturesApiResponse = /** status 200  */ AutofillFeaturesResponseBody;
export type GetAutofillFeaturesApiArg = {
  vin: string;
  decoderType?: string | null;
};
export type PutAutofillFeaturesApiResponse = /** status 200  */ AutofillFeaturesResponseBody;
export type PutAutofillFeaturesApiArg = {
  vehicleId: string;
};
export type AutofillFeaturesResponseBody = {
  features: string[];
};
export type RotateVehiclePhotoApiArg = {
  vehicleId: string;
  vehicleAlbumId: string;
  vehiclePhotoIds: string[];
  angle: 90 | 180 | 270;
};
export type RotateVehiclePhotoApiRes = {vehiclePhotoIds: string[]};
export type GetBackgroundRemovalListApiResponse =
  /** status 200  */ GetBackgroundRemovalListItemResponseBody[];
export type GetBackgroundRemovalListItemResponseBody = {
  id: string;
  name: string;
  backgroundId: string;
};
export type PutBackgroundRemovalSettingsApiResponse = /** status 204  */ undefined;
export type PutBackgroundRemovalSettingsApiArg = {
  body: BackgroundRemovalSettingsRequestBody;
};
export type BackgroundRemovalSettingsRequestBody = {
  backgroundId: string;
  registrationPlateRemovalEnabled: boolean;
  windowTintingEnabled: boolean;
};
export type GetVehicleAlbumPhotosApiResponse = /** status 200  */ VehicleAlbumPhotosResponseBody[];
export type GetVehicleAlbumPhotosApiArg = {
  vehicleId: string;
  vehicleAlbumId: string;
};
export type VehicleAlbumPhotosResponseBody = {
  id: string;
  weight: number;
  fileId: string;
  remoteFileId: string;
  isCoverPhoto: boolean;
  url: string;
  latestFileOperation: FileOperationResponseBody | null;
};
export type GetVehicleAlbumPhotoListApiResponse =
  /** status 200  */ VehicleAlbumPhotoV2ResponseBody[];
export type GetVehicleAlbumPhotoListApiArg = {
  vehicleId: string;
  vehicleAlbumId: string;
};
export type VehicleAlbumPhotoV2ResponseBody = {
  id: string;
  weight: number;
  fileId: string;
  remoteFileId: string;
  isCoverPhoto: boolean;
  url: string;
  latestFileOperation: FileOperationResponseBody | null;
  thumbnailUrl: string | null;
  variants: AlbumVideoVariantResponseBody[] | null;
  mediaType: AlbumMediaType;
};
export type SetVehicleAlbumCoverPhotoApiResponse = /** status 204  */ undefined;
export type SetVehicleAlbumCoverPhotoApiArg = {
  vehicleAlbumId: string;
  vehiclePhotoId: string;
  vehicleId: string;
};
export type UnsetVehicleAlbumCoverPhotoApiResponse = /** status 204  */ undefined;
export type UnsetVehicleAlbumCoverPhotoApiArg = {
  vehicleAlbumId: string;
  vehicleId: string;
};
export type CreatePriceReportRequestApiResponse = /** status 202  */ CreateResponseBody;
export type CreatePriceReportRequestApiArg = {
  vehicleId: string;
  body: CreateVehiclePriceReportRequestBody;
};
export type PriceReportPreviewResponseBody = {
  stock: number | null;
  sold: number | null;
  recommendedSellingPrice: Money | null;
};
export type CreateVehiclePriceReportPreviewApiResponse = PriceReportPreviewResponseBody;
export type CreateVehiclePriceReportPreviewApiArg = {
  vehicleId: string;
  createVehiclePriceReportRequestBody: CreateVehiclePriceReportRequestBody;
};
export type CreateResponseBody = {
  id: string | null;
  requestId: string | null;
  url: string;
  createdAt: string;
};
export type GetVehicleInsuranceApiResponse = /** status 200  */ VehicleInsuranceResponseBody;
export type GetVehicleInsuranceApiArg = {
  vehicleId: string;
};
export type SetVehicleInsuranceApiArg = {
  vehicleId: string;
  data: VehicleInsuranceResponseBody;
};
export type ListTestDriveApiResponseVehicle = /** status 200  */ TestDriveResponseBody[];
export type ListTestDriveApiArgVehicle = {
  vehicleId: string;
};

export type GetAuditsApiResponse = /** status 200  */ AuditResponseBody[];
export type GetAuditsApiArg = {
  vehicleId: string;
};
export type SetVehicleCustomIdApiResponse = /** status 204  */ void;
export type SetVehicleCustomIdApiArg = {
  vehicleId: string;
  setVehicleCustomIdRequestBody: SetVehicleCustomIdRequestBody;
};
export type SetVehicleCustomIdRequestBody = {
  customId: string | null;
};

export type CopyAuditPhotoToAlbumApiResponse = /** status 204  */ void;
export type CopyAuditPhotoToAlbumApiArg = {
  vehicleId: string;
  body: CopyAuditPhotoToAlbumRequestBody;
};

export interface GetVehicleHomeServiceResponse {
  branchId: string;
}
export interface GetVehicleHomeServiceArg {
  vehicleId: string;
}

export interface PutVehicleHomeServiceArg {
  vehicleId: string;
  body: PutVehicleHomeServiceBody;
}
export interface PutVehicleHomeServiceBody {
  branchId: string;
}
export interface PutVehicleHomeServiceResponse {
  branchId: string;
}

export type ChangeVehicleTypeApiResponse = unknown;
export type ChangeVehicleTypeApiArg = {
  vehicleId: string;
  type: VehicleTypeEnum;
};
export type CreateBackgroundRemovalApiResponse = /** status 201  */ UuidResponseBody;
export type CreateBackgroundRemovalApiArg = {
  createBackgroundRemovalRequestBody: CreateBackgroundRemovalRequestBody;
};
export type CreateBackgroundRemovalRequestBody = {
  name: string;
  backgroundId: string;
  registrationPlateRemovalEnabled: boolean;
  windowTintingEnabled: boolean;
  registrationPlateRemovalFileId: string | null;
};
export type GetBackgroundRemovalV2ApiResponse = /** status 200  */ GetBackgroundRemovalResponseBody;
export type GetBackgroundRemovalResponseBody = {
  id: string;
  name: string;
  backgroundId: string;
  backgroundReplacementEnabled: boolean;
  registrationPlateRemovalEnabled: boolean;
  windowTintingEnabled: boolean;
  registrationPlateRemovalFileLink: string | null;
  registrationPlateRemovalFileId: string | null;
};
export type GetBackgroundRemovalV2ApiArg = {
  settingId: string;
};
export type UpdateBackgroundRemovalV2ApiResponse = unknown;
export type UpdateBackgroundRemovalV2ApiArg = {
  settingId: string;
  updateBackgroundRemovalRequestBody: UpdateBackgroundRemovalRequestBody;
};
export type UpdateBackgroundRemovalRequestBody = {
  name: string;
  backgroundId: string;
  registrationPlateRemovalEnabled: boolean;
  windowTintingEnabled: boolean;
  registrationPlateRemovalFileId: string | null;
};
export type DeleteBackgroundRemovalApiResponse = unknown;
export type DeleteBackgroundRemovalApiArg = {
  settingId: string;
};
export type CreateWatermarkApiResponse = /** status 201  */ UuidResponseBody;
export type CreateWatermarkApiArg = {
  createWatermarkRequestBody: CreateWatermarkRequestBody;
};
export type CreateWatermarkRequestBody = {
  name: string;
  fileId: string | null;
  position: WatermarkPositionEnum;
  size: number;
  fitToImage: boolean;
  addOnlyForFirstPhoto: boolean;
  setToAllVehicles?: boolean;
  republishAds?: boolean;
};
export type DeleteWatermarkApiResponse = unknown;
export type DeleteWatermarkApiArg = {
  watermarkId: string;
  deleteWatermarkRequestBody: DeleteWatermarkRequestBody;
};
export type DeleteWatermarkRequestBody = {
  replacementWatermarkId?: string | null;
  republishAds?: boolean;
};
export type GetWatermarkDetailApiResponse = /** status 200  */ GetWatermarkDetailResponseBody;
export type GetWatermarkDetailResponseBody = {
  id: string;
  name: string;
  fileId: string | null;
  imageUri: string | null;
  position: WatermarkPositionEnum;
  size: number;
  fitToImage: boolean;
  addOnlyForFirstPhoto: boolean;
  usageType: 'ONLY_FIRST_PHOTO' | 'ONLY_INTERIOR_PHOTOS' | 'ONLY_EXTERIOR_PHOTOS' | 'ALL_PHOTOS';
  composition: CompositionScene | null;
  // null = every vehicle in sale.
  vehicleSelection: CampaignFilter | null;
  autoAssign: boolean;
};
export type GetWatermarkDetailApiArg = {
  watermarkId: string;
};
export type UpdateWatermarkDetailApiResponse = unknown;
export type UpdateWatermarkDetailApiArg = {
  watermarkId: string;
  updateWatermarkRequestBody: UpdateWatermarkDetailRequestBody;
};
export type UpdateWatermarkDetailRequestBody = {
  name: string;
  fileId: string | null;
  position: WatermarkPositionEnum;
  size: number;
  fitToImage: boolean;
  addOnlyForFirstPhoto: boolean;
  republishAds?: boolean;
};
export type GetWatermarkListApiResponse = /** status 200  */ GetWatermarkListItemResponseBody[];
export type GetWatermarkListItemResponseBody = {
  id: string;
  name: string;
  composition: CompositionScene | null;
};

export type ServiceAlbumResponseBody = {
  id: string;
  description: string | null;
  serviceCaseId: string;
  createdAt: string;
};
export type GetServiceAlbumListApiResponse = /** status 200  */ ServiceAlbumResponseBody[];
export type GetServiceAlbumListApiArg = {
  vehicleId: string;
  serviceCaseId?: string | null;
};

export type RenameServiceAlbumRequestBody = {
  description: string;
};
export type RenameServiceAlbumApiResponse = /** status 204  */ undefined;
export type RenameServiceAlbumApiArg = {
  vehicleId: string;
  serviceAlbumId: string;
  renameServiceAlbumRequestBody: RenameServiceAlbumRequestBody;
};

// Shared media schemas (BE `FileType` / `VideoVariantResponseBody`) used by both the
// service-album and vehicle-album (V2) photo list responses.
export type AlbumMediaType = 'image' | 'video' | 'unknown';

export type AlbumVideoVariantResponseBody = {
  type: string;
  status: string;
  url: string;
};

export type ServiceAlbumPhotoResponseBody = {
  id: string;
  weight: number;
  fileId: string;
  remoteFileId: string;
  url: string;
  latestFileOperation: FileOperationResponseBody | null;
  mediaType: AlbumMediaType;
  thumbnailUrl: string | null;
  variants: AlbumVideoVariantResponseBody[] | null;
};

export type GetServiceAlbumPhotoListApiResponse =
  /** status 200  */ ServiceAlbumPhotoResponseBody[];
export type GetServiceAlbumPhotoListApiArg = {
  vehicleId: string;
  serviceAlbumId: string;
};

export type DownloadServiceGalleryApiResponse = /** status 200  */ DownloadGalleryResponseBody;
export type DownloadServiceGalleryApiArg = {
  vehicleId: string;
  serviceAlbumId: string;
  maxBytes?: number;
};

export type PutVehicleToSaleArg = {vehicleId: string};
export type PutVehicleToSaleApiResponse = void;

export type AddFilesToServiceAlbumRequestBody = {
  fileIds: string[];
  withEmptyUrl?: boolean;
};
export type AddFilesToServiceAlbumApiResponse = /** status 204  */ void;
export type AddFilesToServiceAlbumApiArg = {
  vehicleId: string;
  serviceAlbumId: string;
  addFilesToServiceAlbumRequestBody: AddFilesToServiceAlbumRequestBody;
};

export type DeleteServicePhotoApiResponse = unknown;
export type DeleteServicePhotoApiArg = {
  vehicleId: string;
  serviceAlbumId: string;
  servicePhotoId: string;
};
export type ListHighlightsForTenantApiResponse = /** status 200  */ TagResponseBody[];
export type ListHighlightsForTenantApiArg = {
  size?: number;
  offset?: number;
};
export type ListShortcomingsForTenantApiResponse = /** status 200  */ TagResponseBody[];
export type ListShortcomingsForTenantApiArg = {
  size?: number;
  offset?: number;
};

export type GetVehicleListApiResponse = /** status 200  */ ShortVehicleResponseBody[];
export type GetVehicleListApiArg = {
  size?: number;
  offset?: number;
  customerId?: string | null;
  vin?: string | null;
  registerNumber?: string | null;
  publicId?: string | null;
  externalId?: string | null;
};
export type CustomerResponseBody = {
  uuid: string;
  customerCode: string | null;
  countryCode: string | null;
  sourceCode: string | null;
  legalForm: string;
  typeCodes: string[];
};
export type OwnershipTypeResponseBody = {
  id: string;
  name: string;
  unique: boolean;
  typeLabel: string;
};
export type ShortOwnershipResponseBody = {
  customer: CustomerResponseBody;
  type: OwnershipTypeResponseBody;
};
export type ShortStateResponseBody = {
  registrationPlate: string | null;
};
export type ShortVehicleResponseBody = {
  id: string;
  ownerships: ShortOwnershipResponseBody[];
  state?: ShortStateResponseBody | null;
  vin?: string | null;
  make: string;
  modelFamily: string;
  type?: string | null;
  bodyStyle?: string | null;
  publicId?: string | null;
  externalId?: string | null;
};
export type VehiclePhotoPositionDto = {
  fileId: string;
  position: number;
};
export type VehiclePhotoCopyRequestBody = {
  photos: VehiclePhotoPositionDto[];
  sourceAlbum: string;
  destinationAlbum: string;
  position?: number | null;
};
export type VehiclePhotoCopyApiResponse = /** status 204  */ void;
export type VehiclePhotoCopyApiArg = {
  vehicleId: string;
  vehiclePhotoCopyRequestBody: VehiclePhotoCopyRequestBody;
};
export type ApiVehiclePhotoDeleteApiResponse = /** status 204  */ void;
export type ApiVehiclePhotoDeleteApiArg = {
  vehicleId: string;
  albumId: string;
  vehiclePhotoDeleteRequestBody: VehiclePhotoDeleteRequestBody;
};
export type VehiclePhotoUpdateAlbumRequestBody = {
  photos: VehiclePhotoPositionDto[];
  sourceAlbum: string;
  destinationAlbum: string;
  position?: number | null;
};
export type VehiclePhotoUpdateAlbumApiResponse = /** status 204  */ void;
export type VehiclePhotoUpdateAlbumApiArg = {
  vehicleId: string;
  vehiclePhotoUpdateAlbumRequestBody: VehiclePhotoUpdateAlbumRequestBody;
};
// Service-case photo copy/move: same wire contract as the vehicle photo endpoints above,
// but resolves service-case (VehicleAlbum) album/photo ids instead of sale-vehicle ids.
export type CopyServicePhotoApiArg = {
  vehicleId: string;
  vehiclePhotoCopyRequestBody: VehiclePhotoCopyRequestBody;
};
export type MoveServicePhotoApiArg = {
  vehicleId: string;
  vehiclePhotoUpdateAlbumRequestBody: VehiclePhotoUpdateAlbumRequestBody;
};

export type ServicePhotoDeleteRequestBody = {
  photoIds: string[];
};
export type DeleteServicePhotosApiResponse = /** status 204  */ void;
export type DeleteServicePhotosApiArg = {
  vehicleId: string;
  serviceAlbumId: string;
  servicePhotoDeleteRequestBody: ServicePhotoDeleteRequestBody;
};

export type MileageValidationResultResponseBody = {
  actualMileage: number;
  delta: number;
};

export type GetVehicleMileageValidationApiResponse = MileageValidationResultResponseBody;
export type GetVehicleMileageValidationApiArg = {
  vehicleId: string;
  mileage: number;
};

export type AuthorResponseBody = {
  id: string | null;
  firstName: string | null;
  lastName: string | null;
};

export type SourceResponseBody = {
  id: string | null;
  name: string | null;
  action: string;
};

export type VehicleMileageResponseBody = {
  vehicleId: string;
  mileage: number;
  delta: number;
  datedOn: string;
  author: AuthorResponseBody;
  source: SourceResponseBody;
  note: string | null;
};

export type GetVehicleMileageApiResponse = /** status 200  */ VehicleMileageResponseBody;
export type GetVehicleMileageApiArg = {
  vehicleId: string;
};

export type VehicleMileageAction =
  | 'service'
  | 'car-rental-rent'
  | 'car-rental-return'
  | 'technical-condition'
  | 'vehicle-edit'
  | 'vehicle-create'
  | 'correction'
  | 'test-drive-handover'
  | 'test-drive-return';

export type WriteMileageRequestBody = {
  id: string;
  mileage: number;
  action: VehicleMileageAction;
  title: string;
  note?: string | null;
};

export type WriteVehicleMileageApiResponse = /** status 204  */ void;
export type WriteVehicleMileageApiArg = {
  vehicleId: string;
  writeMileageRequestBody: WriteMileageRequestBody;
};

export type CorrectMileageRequestBody = {
  mileage: number;
  note?: string | null;
};

export type CorrectVehicleMileageApiResponse = /** status 204  */ void;
export type CorrectVehicleMileageApiArg = {
  vehicleId: string;
  correctMileageRequestBody: CorrectMileageRequestBody;
};

export type AutotracerPlReportResponseBody = {
  id: string;
  status: 'accepted' | 'pending' | 'completed' | 'canceled' | 'error';
  url: string | null;
  pdfUrl: string | null;
  createdAt: string;
  createdBy: string;
};

export type CreateAutotracerPlReportApiResponse = /** status 201  */ UuidResponseBody;
export type CreateAutotracerPlReportApiArg = {
  vehicleId: string;
};

export type GetVehicleAutotracerPlReportsApiResponse =
  /** status 200  */ AutotracerPlReportResponseBody[];
export type GetVehicleAutotracerPlReportsApiArg = {
  vehicleId: string;
};

export type GetAutotracerPlReportApiResponse = /** status 200  */ AutotracerPlReportResponseBody;
export type GetAutotracerPlReportApiArg = {
  vehicleId: string;
  reportId: string;
};

export type AutotracerPlPreviewResponseBody = {
  status: 'pending' | 'completed';
  isMileageAvailable: boolean | null;
  isDamagesAvailable: boolean | null;
  isPhotoHistoryAvailable: boolean | null;
  isOriginAvailable: boolean | null;
  isEquipmentListAvailable: boolean | null;
  isTechDataListAvailable: boolean | null;
};

export type GetAutotracerPlPreviewApiResponse = /** status 200  */ AutotracerPlPreviewResponseBody;
export type GetAutotracerPlPreviewApiArg = {
  vehicleId: string;
};
